import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import rubikFont from '../../fonts/Rubik-Regular.ttf';
import rubikFontBold from '../../fonts/Rubik-Bold.ttf';
import { downloadSignature, downloadLogo } from '../../Services/http/PackingListService';
import { TPackingListPdfData, TShipmentAddress, TShipmentItem } from '../../types/shipment.type';

// Helper function to load and embed fonts
const loadFonts = async (pdfDoc: PDFDocument) => {
  const rubikFontBytes = await fetch(rubikFont).then(res => res.arrayBuffer());
  const rubikFontBoldBytes = await fetch(rubikFontBold).then(res => res.arrayBuffer());
  const rubik = await pdfDoc.embedFont(rubikFontBytes);
  const rubikBold = await pdfDoc.embedFont(rubikFontBoldBytes);
  return { rubik, rubikBold };
};

// Draws the header section with details and logo
const drawHeaderSection = async (
  pdfDoc: PDFDocument,
  page: any,
  packingList: any,
  fonts: any,
  width: number,
  height: number,
  shipment_id: string,
  createdAt: string,
  skuTotal: number,
  unitTotal: number
) => {
  page.drawRectangle({
    x: 0,
    y: height - 180,
    width,
    height: 180,
    color: rgb(0.961, 0.961, 0.953), // Light grey background
  });

  if (packingList.logo_url) {
    await drawLogo(pdfDoc, page, packingList.logo_url, height);
  }

  page.drawText('PACKING LIST', {
    x: 220,
    y: height - 70,
    size:20,
    font: fonts.rubikBold,
    color: rgb(0, 0, 0),
  });

  page.drawText(`Date: ${createdAt ? new Date(createdAt).toLocaleDateString() : ''}`, {
    x: 220,
    y: height - 110,
    size: 10,
    font: fonts.rubik,
    color: rgb(0, 0, 0),
  });
  page.drawText(`Shipment ID: ${shipment_id}`, {
    x: 220,
    y: height - 125,
    size: 10,
    font: fonts.rubik,
    color: rgb(0, 0, 0),
  });
  page.drawText(`SKU Total: ${skuTotal}`, {
    x: 220,
    y: height - 140,
    size: 10,
    font: fonts.rubik,
    color: rgb(0, 0, 0),
  });
  page.drawText(`Unit Total: ${unitTotal}`, {
    x: 220,
    y: height - 155,
    size: 10,
    font: fonts.rubik,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: 50, y: height - 180 },
    end: { x: width - 50, y: height - 180 },
    thickness: 1,
    color: rgb(0, 0, 0),
  });
};

// Function to draw logo with maintained proportions and adjusted size/position
const drawLogo = async (pdfDoc: PDFDocument, page: any, logoUrl: string, height: number) => {
  const logoBase64 = await downloadLogo(logoUrl);
  if (logoBase64) {
    const logoImage = await pdfDoc.embedPng(logoBase64);

    const logoX = 50; // Horizontal position of the logo
    const logoY = height - 120; // Move higher by decreasing this value
    const logoWidth = 150; // Increase the width to make it larger
    const aspectRatio = logoImage.height / logoImage.width;
    const logoHeight = logoWidth * aspectRatio;  // Maintain aspect ratio

    // Draw logo without a border
    page.drawImage(logoImage, {
      x: logoX,
      y: logoY,
      width: logoWidth,
      height: logoHeight,
    });
  }
};

// Draw shipping details for "Ship From" and "Ship To" sections
const drawShippingDetails = (page: any, fonts: any, shipFrom: TShipmentAddress, shipTo: TShipmentAddress, height: number) => {
  let textYPosition = height - 220;

  // "Ship From" details
  page.drawText("Ship From:", {
    x: 50,
    y: textYPosition,
    size: 12,
    font: fonts.rubikBold,
    color: rgb(0, 0, 0),
  });
  textYPosition -= 15;
  page.drawText(`${shipFrom?.name || ''}`, { x: 50, y: textYPosition, size: 10, font: fonts.rubik });
  textYPosition -= 12;
  page.drawText(`${shipFrom?.address || ''}`, { x: 50, y: textYPosition, size: 10, font: fonts.rubik });
  textYPosition -= 12;
  page.drawText(
    `${shipFrom?.city || ''}${shipFrom?.city && shipFrom?.state ? ', ' : ''}${shipFrom?.state || ''}`,
    { x: 50, y: textYPosition, size: 10, font: fonts.rubik }
  );
  textYPosition -= 12;
  page.drawText(
    `${shipFrom?.country || ''}${shipFrom?.country && shipFrom?.zip_code ? ', ' : ''}${shipFrom?.zip_code || ''}`,
    { x: 50, y: textYPosition, size: 10, font: fonts.rubik }
  );

  // "Ship To" details
  page.drawText("Ship To:", {
    x: 350,
    y: height - 220,
    size: 12,
    font: fonts.rubikBold,
    color: rgb(0, 0, 0),
  });
  textYPosition = height - 235;
  page.drawText(`${shipTo?.name || ''}`, { x: 350, y: textYPosition, size: 10, font: fonts.rubik });
  textYPosition -= 12;
  page.drawText(`${shipTo?.address || ''}`, { x: 350, y: textYPosition, size: 10, font: fonts.rubik });
  textYPosition -= 12;
  page.drawText(
    `${shipTo?.city || ''}${shipTo?.city && shipTo?.state ? ', ' : ''}${shipTo?.state || ''}`,
    { x: 350, y: textYPosition, size: 10, font: fonts.rubik }
  );
  textYPosition -= 12;
  page.drawText(
    `${shipTo?.country || ''}${shipTo?.country && shipTo?.zip_code ? ', ' : ''}${shipTo?.zip_code || ''}`,
    { x: 350, y: textYPosition, size: 10, font: fonts.rubik }
  );
};

// Adds headers for the table with a black background and white text
const addTableHeaders = (page: any, fonts: any, yPosition: number, columnWidths: number[]) => {
  const tableHeaders = ['Item', 'SKU', 'FNSKU', 'ASIN', 'Description', 'Quantity Shipped'];
  const headerHeight = 20;

  page.drawRectangle({
    x: 10,
    y: yPosition - headerHeight + 14,
    width: columnWidths.reduce((a, b) => a + b, 0),
    height: headerHeight,
    color: rgb(0, 0, 0),
  });

  tableHeaders.forEach((header, i) => {
    page.drawText(header, {
      x: 10 + columnWidths.slice(0, i).reduce((a, b) => a + b, 0) + (columnWidths[i] / 2) - (fonts.rubikBold.widthOfTextAtSize(header, 10) / 2),
      y: yPosition,
      size: 10,
      font: fonts.rubikBold,
      color: rgb(1, 1, 1),
    });
  });

  return yPosition - 7;
};

// Draw table rows with cell borders
const drawTableRows = (pdfDoc: PDFDocument, fonts: any, shipmentItems: TShipmentItem[], page: any, yPosition: number, columnWidths: number[], height: number) => {
  let currentPage = page;

  shipmentItems.forEach((shipmentItem, index) => {
    const descriptionLines = splitTextByWidth(shipmentItem.description || '', columnWidths[4] - 20, fonts.rubik, 10);
    const rowHeight = Math.max(descriptionLines.length * 14 + 20, 20);

    // Check if we need a new page for the next row
    if (yPosition - rowHeight < 50) {
      currentPage = pdfDoc.addPage([600, 800]);
      yPosition = 750;
      addTableHeaders(currentPage, fonts, yPosition, columnWidths);
      yPosition -=7;
    }

    // Draw cell borders for each column in the row
    columnWidths.reduce((x, width, i) => {
      currentPage.drawRectangle({
        x: x,
        y: yPosition - rowHeight,
        width: width,
        height: rowHeight,
        borderColor: rgb(0, 0, 0),
        borderWidth: 0.1,
      });
      return x + width;
    }, 10);

    // Center-align content within cells with padding
    const verticalCenter = yPosition - (rowHeight / 2) ;

    currentPage.drawText(`${index + 1}`, {
      x: 10 + (columnWidths[0] / 2) - (fonts.rubik.widthOfTextAtSize(`${index + 1}`, 10) / 2),
      y: verticalCenter,
      size: 10,
      font: fonts.rubik,
      color: rgb(0, 0, 0),
    });
    currentPage.drawText(shipmentItem.sku ||'', {
      x: 10 + columnWidths[0] + (columnWidths[1] / 2) - (fonts.rubik.widthOfTextAtSize(shipmentItem.sku||'', 10) / 2),
      y: verticalCenter,
      size: 9,
      font: fonts.rubik,
      color: rgb(0, 0, 0),
    });
    currentPage.drawText(shipmentItem.fnsku||'', {
      x: 10 + columnWidths[0] + columnWidths[1] + (columnWidths[2] / 2) - (fonts.rubik.widthOfTextAtSize(shipmentItem.fnsku||'', 10) / 2),
      y: verticalCenter,
      size: 9,
      font: fonts.rubik,
      color: rgb(0, 0, 0),
    });
    currentPage.drawText(shipmentItem.asin||'', {
      x: 10 + columnWidths[0] + columnWidths[1] + columnWidths[2] + (columnWidths[3] / 2) - (fonts.rubik.widthOfTextAtSize(shipmentItem.asin||'', 10) / 2),
      y: verticalCenter,
      size: 9,
      font: fonts.rubik,
      color: rgb(0, 0, 0),
    });

    descriptionLines.forEach((line, lineIndex) => {
      currentPage.drawText(line, {
        x: 10 + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + 10,
        y: yPosition - 13 - (lineIndex * 14),
        size: 9,
        font: fonts.rubik,
        color: rgb(0, 0, 0),
      });
    });

    currentPage.drawText(`${shipmentItem.quantity_shipped}`, {
      x: 10 + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3] + columnWidths[4] + (columnWidths[5] / 2) - (fonts.rubik.widthOfTextAtSize(`${shipmentItem.quantity_shipped}`, 10) / 2),
      y: verticalCenter,
      size: 8,
      font: fonts.rubik,
      color: rgb(0, 0, 0),
    });

    yPosition -= rowHeight;
  });

  return { currentPage, yPosition };
};

// Draws the signature and name section on the last page
const drawSignatureSection = async (
  pdfDoc: PDFDocument,
  fonts: any,
  lastPage: any,
  signatureUrl: string,
  name: string,
  yPosition: number
) => {
  if (yPosition < 100) {
    lastPage = pdfDoc.addPage([600, 800]);
    yPosition = 700;
  }

  if (signatureUrl) {
    const signatureBase64 = await downloadSignature(signatureUrl);
    if (signatureBase64) {
      const signatureImage = await pdfDoc.embedPng(signatureBase64);

      lastPage.drawRectangle({
        x: 50,
        y: yPosition,
        width: 200,
        height: 70,
        borderColor: rgb(0, 0, 0),
        borderWidth: 1,
      });

      lastPage.drawText("Name:", {
        x: 60,
        y: yPosition + 40,
        size: 12,
font: fonts.rubikBold,
        color: rgb(0, 0, 0),
      });

      lastPage.drawText(name || '', {
        x: 60,
        y: yPosition + 20,
        size: 16,
font: fonts.rubik,
        color: rgb(0, 0, 0),
      });

      lastPage.drawRectangle({
        x: 270,
        y: yPosition,
        width: 200,
        height: 70,
        borderColor: rgb(0, 0, 0),
        borderWidth: 1,
      });

      lastPage.drawImage(signatureImage, {
        x: 280,
        y: yPosition + 10,
        width: 180,
        height: 50,
      });
    }
  }
};

// Helper function to split text by width for table cells
const splitTextByWidth = (text: string, maxWidth: number, font: any, fontSize: number) => {
  const words = text.split(' ');
  let lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = font.widthOfTextAtSize(testLine, fontSize);

    if (testWidth < maxWidth) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

// Main function to generate PDF
export const generatePackingListPdf = async (packingListPdfData: TPackingListPdfData) => {
  const {
    shipment_id,
    packingList,
    created_at,
    skuTotal,
    unitTotal,
    ship_from,
    ship_to,
    shipmentItems,
  } = packingListPdfData;

  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);
  const fonts = await loadFonts(pdfDoc);
  let page = pdfDoc.addPage([600, 800]);
  const { width, height } = page.getSize();

  await drawHeaderSection(pdfDoc, page, packingList, fonts, width, height, shipment_id, created_at, skuTotal, unitTotal);
  drawShippingDetails(page, fonts, ship_from, ship_to, height);

  const columnWidths = [40, 80, 80, 80, 180, 95];
  let yPosition = height - 320;
  yPosition = addTableHeaders(page, fonts, yPosition, columnWidths);

  const { currentPage, yPosition: finalYPosition } = drawTableRows(pdfDoc, fonts, shipmentItems, page, yPosition, columnWidths, height);

await drawSignatureSection(pdfDoc, fonts, currentPage, packingList.signature_url, packingList.name, Math.max(finalYPosition - 80, 30));

  return await pdfDoc.save();
};
